  /**
   * @memberof SMART2
   * @ngdoc directive
   * @name Select
   * @description This directive is useful for creating a select ui element.
   * 
   * @attr {String} label
   *    Label to be displayed for identification of this ui element
   * @attr {Array} options
   *    Number of options to be created
   * @attr {Var} ng-model
   *    Default selected option. Please refer example for more details.
   * @attr {Object} ng-model-options
   *    https://docs.angularjs.org/api/ng/directive/ngModelOptions. 
   *    <a href="SMART2.Textfield.html">Click here</a> and scoll down to 'Textfield with ng-model-options' to see example reference.
   * @attr {String} datakey
   *    ng-model variable will be updated on the basis of this property. datakey will be used to display an option when displaykey is not set.
   * @attr {String} displaykey
   *    A property of an option to be used to display an option
   * @attr {Boolean} disable
   *    If set to true, none of the options can be selected
   * @attr {Boolean} focus 
   *    If set to true, this ui element will be focused
   * @attr {Boolean} validate
   *    If set to true, this ui element will be validated on the basis of rules passed to it
   * @attr {Array} rules 
   *    Rules to be evaluated when this element's blur event is fired. A rule must have 'rule' and 'errorMessage' properties (keys).
   *    'rule' must be a condition or group of conditions. 'errorMessage' will be the message to be displayed when corresponding rule fails. See example for more.
   * @attr {String} error-message 
   *    Error message to be displayed. This attribute can be set at any point to display an error message.
   * @attr {Boolean} is-mandatory 
   *    If set to true, default error message will be displayed when blur event is fired and this ui element is left blank
   * @attr {Boolean} is-visible 
   *    If set to true, this ui element will be displayed on form regardless of 'is-mandatory' property's value. 
   *    This ui element can be removed from form by clicking 'x' button on top right corner of this ui element.
   * @attr {Number} colspan
   *    Number of columns to be occupied by this ui element. 1 column is equal to 1 ui element.
   *    Default value is 1.
   *    For e.g. If colspan is set to 2, this ui element will occupy width of 2 ui elements (fields).
   * @attr {Function} on-change
   *    Callback function when option is changed
   * 
   * @example
   Dynamic:
   Controller:
       $scope.config = {
            "modelData": {
                "currency": { "code": "€", "name": "EUR" }
            }, 
            "formConfig": {
                "sections": [
                    {
                        "isMandatory": true,
                        "rows": [
                            {
                                "properties": [
                                    {
                                        "label": "Currency",
                                        "type": "dropdown",      //  select will also work
                                        "isMandatory": true,
                                        "data": "currency",
                                        "colspan": 1,
                                        "onChange": "onChange",  //  controller function
                                        "attributes": {
                                            "options": [{
                                                "code": "$",
                                                "name": "USD"
                                            }, {
                                                "code": "€",
                                                "name": "EUR"
                                            }],
                                            "datakey": "name"
                                        },
                                        "rules": [
                                            { 
                                                "rule": "this.title == \"Option 2\"", 
                                                "error": "Invalid selection" 
                                            }
                                        ]
                                    }
                                ]    
                            }
                        ]
                    }
                ]
            }
        };
    Usage:
        <smart-form-widget form-config="config.formConfig" model-data="config.modelData"></smart-form-widget>

   * @example
   Static:
   Controller:
       $scope.currencyOptions = [{
            "code": "$",
            "name": "USD"
       }, {
           "code": "€",
           "name": "EUR"
       }];
       $scope.selectedCurrency = { "code": "€", "name": "EUR" };
       $scope.onChange = function(selectedCurrency) {
           console.log(selectedCurrency);
       };
   Usage:
       <smart-select label="Currency" ng-model="selectedCurrency" options="currencyOptions" on-change="onChange(selectedCurrency)"></smart-select> 

   * @example
   Select with disabled state:
   <smart-select label="Currency" ng-model="selectedCurrency" options="currencyOptions" datakey="name" disable="true"></smart-radio> 

   * @example
   Select with ng-model as 'String' (In following example, when an option is selected, ng-model would be either '$' or '€'):
   Controller:
       $scope.currencyOptions = [{
            "code": "$",
            "name": "USD"
       }, {
           "code": "€",
           "name": "EUR"
       }];
       $scope.selectedCurrency = "$";
       $scope.onChange = function(selectedCurrency) {
           console.log(selectedCurrency);
       };
   Usage:
       <smart-select label="Currency" ng-model="selectedCurrency" options="currencyOptions" datakey="code" displaykey="name" on-change="onChange(selectedCurrency)"></smart-select> 

   * @example
   Select with disabled option:
   Controller:
       $scope.currencyOptions = [{
            "code": "$",
            "name": "USD",
            "disable": true
       }, {
           "code": "€",
           "name": "EUR"
       }];
       $scope.selectedCurrency = { "code": "€", "name": "EUR" };
       $scope.onChange = function(selectedCurrency) {
           console.log(selectedCurrency);
       };
   Usage:
       <smart-select label="Currency" ng-model="selectedCurrency" options="currencyOptions" datakey="name" on-change="onChange(selectedCurrency)"></smart-select> 
   */

(function() {
    'use strict';
    var selectId = 0;
    angular.module('SMART2').directive('smartSelect', ['$timeout', '$window', '$translate', 'ScrollTo', 'RuleEngine', 'smartSelectService', 'formWidgetUtils',
        (typeof isDirectiveV2enabled !== 'undefined' ? (isDirectiveV2enabled() === true || isDirectiveV2enabled() === 'V3' ? smartSelectFunc2 : smartSelectFunc1) : smartSelectFunc1)
    ]);

    function smartSelectFunc1($timeout, $window, $translate, ScrollTo, RuleEngine) {
        return {
            restrict: 'E',
            replace: true,
            require: '?ngModel',
            scope: {
                label: '@',
                options: '=?',
                ngModelCopy: '=?ngModel',
                isMandatory: '@',
                isVisible: '=?',
                rules: '@',
                validate: '=?',
                focus: '=?',
                parentElement: '@',
                onChange: '&',
                disable: '@',
                multiple: '@',
                datakey: '@',
                displaykey: '@',
                removable: '@',
                preview: '@',
                customoption: '@',
                showinfo: '@',
                showwarning: '@',
                customMessage: '@'
            },
            link: function (scope, element, attrs, ngModelCtrl) {
                //debugger;
                var options, isNgModelWatchAdded = false;
                var onNgModel = function () { };
                scope.selectOptions = [];
                var typeOfNgModel;

                var isNgModelFunction = typeof scope.ngModelCopy == 'function';

                // create unique id
                selectId = selectId + 1;
                scope.dropDownId = "dropdown-" + selectId;

                // key to be used to bind options
                scope.dataKey = scope.datakey ? scope.datakey : 'title';
                scope.displayKey = scope.displaykey ? scope.displaykey : scope.dataKey;

                /*
                 *  Following code has been added to make ng-model-options work
                 *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
                 */
                if (isNgModelFunction) {
                    try {
                        ngModelCtrl.$render = function () {
                            scope.ngModel = ngModelCtrl.$modelValue;
                            scope.selectedOption = scope.ngModel;
                        };
                    }
                    catch (e) { }
                }

                                var updateView = function () {
                                    if (isNgModelFunction) {
                                        try {
                                            ngModelCtrl.$setViewValue(scope.ngModel);
                                        }
                                        catch (e) { }
                                    }
                                };
                                /*
                                 * Check if new option is added
                                 */
                                //scope.customOption = 
                                scope.labelVal = "";
                                scope.customOptionReset = false;
                                var onOptions = scope.$watch('options', function (newOptions) {
                                    if (!newOptions) {
                                        return;
                                    }

                                    if (newOptions && newOptions.length > 0) {
                                        //if (scope.label && newOptions[0][scope.dataKey] != scope.label) {
                                        var selLabel;
                                        if (scope.label) {
                                            selLabel = scope.label;
                                        } else {
                                            selLabel = $translate.instant('chooseOptionMsg');
                                        }
                                        var isDisabledOpt = true;
                                        if (scope.customoption) {
                                            selLabel = scope.customoption;
                                            isDisabledOpt = false;
                                            scope.customOptionReset = true;
                                        }
                        if (scope.dataKey === scope.displayKey) {
                            options = [{ title: selLabel, disable: isDisabledOpt, id: 'fakeOptionId' }].concat(newOptions);
                            options[0][scope.displayKey] = options[0].title;
                        }
                        else {
                            var firstOption = { disable: isDisabledOpt, id: 'fakeOptionId' };
                            firstOption[scope.dataKey] = selLabel;
                            firstOption[scope.displayKey] = selLabel;
                            options = [firstOption].concat(newOptions)
                        }

                        //if (scope.$eval(scope.isMandatory)) {
                        //    options = newOptions;
                        //}
                        //else {

                        //    options = [{ title: selLabel, disable: false, id: 'fakeOptionId' }].concat(newOptions);
                        //    options[0][scope.displayKey] = options[0].title;
                        //}
                        //}
                        //else {
                        //    options = newOptions;
                        //}
                    }
                    else {
                        return;
                    }

                                    var optionGroups = [];

                                    for (var i = 0; i < options.length; i++) {
                                        if (options[i].hasOwnProperty('groupName') && optionGroups.indexOf(options[i].groupName) == -1) {
                                            optionGroups.push(options[i].groupName);
                                        }
                                    }

                    //if (optionGroups.length > 0) {
                    //    options.splice(0, 1);
                    //}

                    scope.optionGroups = angular.copy(optionGroups);

                    scope.selectOptions = options;

                    //if (!scope.selectedOption) {
                    //    scope.selectedOption = options[0];
                    //}

                    //try {
                    //    scope.selectedOptionString = angular.copy(scope.selectedOption[scope.dataKey]);
                    //}
                    //catch (e) { }

                    if (!isNgModelWatchAdded) {
                        onNgModel = scope.$watch(isNgModelFunction ? 'ngModel' : 'ngModelCopy', function (newVal) {
                            if (angular.isUndefined(typeOfNgModel) && (newVal !== '' && newVal != undefined && newVal != null)) {
                                typeOfNgModel = typeof newVal;
                            }
                            var newValueCopy;

                                            if (typeOfNgModel == 'string' || typeOfNgModel == 'number') {
                                                for (var i = 0; i < options.length; i++) {
                                                    if (newVal == options[i][scope.dataKey]) {
                                                        newValueCopy = options[i];
                                                        break;
                                                    }
                                                }
                                            }
                                            else {
                                                newValueCopy = newVal;
                                            }
                                            scope.selectedOption = angular.copy(newValueCopy);

                            try {
                                if (!scope.selectedOption) {
                                    scope.selectedOption = angular.copy(options[0]);
                                } else if (!scope.selectedOption[scope.displayKey]) {
                                    scope.selectedOption = angular.copy(options[0]);
                                }
                                scope.selectedOptionString = angular.copy(scope.selectedOption[scope.dataKey]);
                                if (options[0] != scope.selectedOption[scope.dataKey]) {
                                    scope.labelVal = scope.selectedOption[scope.dataKey];
                                    if (scope.customoption && scope.customoption != scope.selectedOption[scope.dataKey]) {
                                        scope.customOptionReset = false;
                                    }
                                }
                            }
                            catch (e) { }

                            setTimeout(function () {
                                var el = angular.element("#" + scope.dropDownId);
                                el.material_select();
                                el.parent().find("input.select-dropdown").off("openSelectList").on("openSelectList", function () {
                                    if (this.value !== '' && this.value !== void 0 && this.value !== null) {
                                    setTimeout(function () {
                                        var $dropdownDiv = el.parent().find(".dropdown-content.select-dropdown.active"),
                                            listElOffset;
                                        $dropdownDiv.scrollTop(0);
                                        listElOffset = $dropdownDiv.find("li").eq(_.indexOf(scope.selectOptions, scope.selectOptions.find(function (obj) {
                                            return obj[scope.dataKey] == (scope.ngModelCopy && scope.ngModelCopy[scope.dataKey]);
                                        }))).offset();
                                        if (listElOffset) {
                                            $dropdownDiv.scrollTop(listElOffset.top - $dropdownDiv.offset().top);
                                        }
                                      }, 0);
                                   }
                                });
                                //el.parent().find("input.select-dropdown").bind("close", function () {
                                //    var _this = $(this);
                                //  scope.ngChange(_this.val());
                                //});
                            });
                            // selectFixdd();

                        }, true);

                        isNgModelWatchAdded = true;
                    }
                    else { //If options are changes dynamically then again .material_select() will fired to update latest options in materialize select.
                        setTimeout(function () {
                            angular.element("#" + scope.dropDownId).material_select();
                            element.parent().find("input.select-dropdown").off("openSelectList").on("openSelectList", function () {
                                if (this.value !== '' && this.value !== void 0 && this.value !== null) {
                                    setTimeout(function () {
                                        var $dropdownDiv = element.parent().find(".dropdown-content.select-dropdown.active"),
                                        listElOffset;
                                        $dropdownDiv.scrollTop(0);
                                        listElOffset = $dropdownDiv.find("li").eq(_.indexOf(scope.selectOptions, scope.selectOptions.find(function (obj) {
                                            return obj[scope.dataKey] == (scope.ngModelCopy && scope.ngModelCopy[scope.dataKey]);
                                        }))).offset();
                                        if (listElOffset) {
                                            $dropdownDiv.scrollTop(listElOffset.top - $dropdownDiv.offset().top);
                                        }
                                    }, 0);
                                            }
                            });
                        });
                    }
                    setTimeout(function () {
                        var el = angular.element("#" + scope.dropDownId);
                        el.children().eq(0).attr("disabled", "disabled");
                        el.material_select();
                        el.parent().find("input.select-dropdown").off("openSelectList").on("openSelectList", function () {
                            if (this.value !== '' && this.value !== void 0 && this.value !== null) {
                                setTimeout(function () {
                                    var $dropdownDiv = el.parent().find(".dropdown-content.select-dropdown.active"),
                                            listElOffset;
                                    $dropdownDiv.scrollTop(0);
                                    listElOffset = $dropdownDiv.find("li").eq(_.indexOf(scope.selectOptions, scope.selectOptions.find(function (obj) {
                                        return obj[scope.dataKey] == (scope.ngModelCopy && scope.ngModelCopy[scope.dataKey]);
                                    }))).offset();
                                    if (listElOffset) {
                                        $dropdownDiv.scrollTop(listElOffset.top - $dropdownDiv.offset().top);
                                    }
                                }, 0);
                            }
                        });
                                        // el.parent().find("input.select-dropdown").bind("close", function () {
                                        //    var _this = $(this);
                                        //    scope.ngChange(_this.val());
                                        //});
                                    });

                    // selectFixdd();

                    /*   function selectFixdd() {
                           setTimeout(function () {
                               if (scope.$eval(attrs.fixed)) {
                                   $(element).find('input.select-dropdown').bind('focus', function (e) {
                                       (function ($t) {
                                           setTimeout(function () {
                                               $(element).find('.dropdown-content').addClass('fixeddd');
                                               var topOffset = $(e.target).offset().top - $(window).scrollTop();
                                               var ddCnt = $t.next('.dropdown-content');
                                               ddCnt.css({
                                                   'left': $(e.target).offset().left + 'px', 'top': topOffset + 'px'
                                               });
                                               ddCnt.attr('style', function (i, s) { return s + 'display: block !important;' });

                                               var ddHgt = ddCnt.css('height'),
                                                   winHgt = angular.element(window).height();
                                               if ((parseInt(ddHgt) + topOffset) > winHgt) {
                                                   var diffHgt = (parseInt(ddHgt) + topOffset) - winHgt;
                                                   ddCnt.css('top', (topOffset - diffHgt - 15) + 'px');
                                               }

                                               angular.element($window).on("scroll.smartFixedSmartSelect", function () {
                                                   if (ddCnt.hasClass('active'))
                                                       ddCnt.hide();
                                               });
                                           });
                                       })($(this));
                                   });
                               }
                           }, 500);
                       }
                       */
                }, true);

                scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);


                /*
                 * Watch needs to be added on disable since element needs to be updated
                 */
                var sOption = scope.label;
                var onDisable = scope.$watch('disable', function (newVal) {
                    setTimeout(function () {
                        var el = angular.element("#" + scope.dropDownId);
                        el.material_select();
                        el.parent().find("input.select-dropdown").off("openSelectList").on("openSelectList", function () {
                            if (this.value !== '' && this.value !== void 0 && this.value !== null) {
                                    setTimeout(function () {
                                        var $dropdownDiv = el.parent().find(".dropdown-content.select-dropdown.active"),
                                            listElOffset;
                                        $dropdownDiv.scrollTop(0);
                                        listElOffset = $dropdownDiv.find("li").eq(_.indexOf(scope.selectOptions, scope.selectOptions.find(function (obj) {
                                            return obj[scope.dataKey] == (scope.ngModelCopy && scope.ngModelCopy[scope.dataKey]);
                                    }))).offset();
                                        if (listElOffset) {
                                            $dropdownDiv.scrollTop(listElOffset.top - $dropdownDiv.offset().top);
                                            }
                                            }, 0);
                            }
                        });
                        el.parent().find("input.select-dropdown").bind("close", function () {
                            /*var _this = $(this);
                            if (scope.ngModelCopy != _this.val()) {
                                scope.ngModelCopy = {
                                    title: _this.val()
                                };
                                scope.ngModelCopy[scope.dataKey] = _this.val();
                            }*/
                            scope.validateRules()
                        });
                    });
                });



                /*
                 * Focus field when focus is set to true
                 */
                var onFocus = scope.$watch('focus', function (newVal) {
                    if (newVal) {
                        ScrollTo.perform(element, angular.element('#' + scope.parentElement));
                        scope.focus = false;
                    }
                });

                /*
                 * Validate field whene validate is set to true
                 */
                var onValidate = scope.$watch('validate', function (newValue) {
                    if (newValue != undefined && newValue) {
                        scope.validateRules();
                    }
                });

                /*
                 * Validate rules
                 */
                scope.validateRules = function () {
                    //debugger;
                    scope.validate = false;
                    scope.errorMessage = null;
                    if (scope.$eval(scope.isMandatory) && scope.label) {
                        if (!scope.ngModelCopy || scope.ngModelCopy.name === scope.label || (scope.customoption && scope.ngModelCopy.name === scope.customoption) || scope.ngModelCopy[scope.dataKey] === scope.label) {
                                            scope.validate = true;
                            scope.errorMessage = scope.customMessage ? scope.customMessage : $translate.instant("selectOptionErrMsg");
                                            return;
                                        }
                                    }
                                    if (scope.rules) {
                                        var rules = scope.$eval(scope.rules);
                                        var isFoundInvalid = false;

                                        for (var i = 0; i < rules.length; i++) {
                                            if (typeof rules[i] == 'object') {
                                                if (eval((rules[i].rule).replace(/this/g, 'scope.selectedOption'))) {
                                                    scope.validate = true;
                                                    scope.errorMessage = rules[i].error;
                                                    break;
                                                }
                                            } else {
                                                if (!isFoundInvalid) {
                                                    RuleEngine.isValid(rules[i], function (e) {
                                                        scope.validate = true;
                                                        scope.errorMessage = e.errorData.error;
                                                        isFoundInvalid = true;
                                                    });
                                                }
                                                else {
                                                    break;
                                                }
                                            }

                        }
                    }
                };
                var ch = false;
                scope.ngChange = function (selectedOption, manual) {
                    if (!manual) ch = true;
                    sOption = selectedOption;
                    if (selectedOption == scope.customoption) {
                        scope.customOptionReset = true;
                        //options[0][scope.dataKey] = scope.label;
                    }
                    else {
                        scope.customOptionReset = false;
                    }

                                    var tmpSelectedOption;
                                    if (!scope.$eval(scope.isMandatory) && selectedOption == options[0][scope.displayKey] && scope.optionGroups.length == 0) {
                                        tmpSelectedOption = '';
                                    }
                                    else {
                                        for (var i = 0; i < options.length; i++) {
                                            if (options[i][scope.dataKey] == selectedOption) {
                                                tmpSelectedOption = options[i];
                                                break;
                                            }
                                        }
                                    }
                                    if (isNgModelFunction) {
                                        if (typeOfNgModel == 'string' || typeOfNgModel == 'number') {
                                            scope.ngModel = tmpSelectedOption[scope.dataKey];
                                        }
                                        else {
                                            scope.ngModel = tmpSelectedOption;
                                        }
                                    }
                                    else {
                                        if (typeOfNgModel == 'string' || typeOfNgModel == 'number') {
                                            if (!scope.$eval(scope.isMandatory) && (selectedOption == options[0][scope.displayKey] || selectedOption == undefined || selectedOption == 'fakeOptionId')) {
                                                scope.ngModelCopy = '';
                                            }
                                            else {
                                                scope.ngModelCopy = tmpSelectedOption[scope.dataKey];
                                            }
                                        }
                                        else {
                                            scope.ngModelCopy = tmpSelectedOption;
                                        }
                                    }
                                    scope.labelVal = "";
                                    if (selectedOption == scope.label && selectedOption == options[0][scope.displayKey]) {
                                        scope.labelVal = selectedOption;
                                    }
                                    updateView();
                                    scope.selectedOption = tmpSelectedOption == '' ? options[0] : angular.copy(tmpSelectedOption);
                                    scope.validateRules();
                                    $timeout(function () {
                                        scope.onChange();
                                    });
                                };


                scope.$on('$destroy', function () {
                    angular.element("#" + scope.dropDownId).material_select('destroy');
                    onOptions();
                    onDisable();
                    onNgModel();
                    onFocus();
                    onValidate();
                    angular.element($window).off("scroll.smartFixedSmartSelect");
                });
            },
            templateUrl: 'shared/directives/uiElements/smartSelect/smartSelectTemplate.html'
        };
    }

    
    function smartSelectFunc2($timeout, $window, $translate, ScrollTo, RuleEngine, smartSelectService, formWidgetUtils) {
        return {
            restrict: 'E',
            replace: true,
            require: '?ngModel',
            scope: {
                label: '@',
                options: '=?',
                ngModelCopy: '=?ngModel',
                isMandatory: '@',
                isVisible: '=?',
                rules: '@',
                validate: '=?',
                focus: '=?',
                parentElement: '@',
                onChange: '&',
                disable: '@',
                multiple: '@',
                datakey: '@',
                displaykey: '@',
                removable: '@',
                preview: '@',
                customoption: '@',
                showinfo: '@',
                showwarning: '@',
                clienterror: '=?',
                customMessage: '@'
            },
            compile: function() {
                return {
                    post: function (scope, element, attrs, ngModelCtrl) {
                        $(document).ready(function () {// To avoid flicker and timeout
                            // Declare all the vars at the top
                            var options = [],
                                isNgModelWatchAdded = false,
                                 onNgModel = function () { },
                                 typeOfNgModel,
                                isNgModelFunction = typeof scope.ngModelCopy == 'function';

                            scope.selectOptions = [];
                            // create unique id
                            selectId = selectId + 1;
                            scope.dropDownId = "dropdown_" + selectId;

                            // key to be used to bind options
                            scope.dataKey = scope.datakey ? scope.datakey : 'title';
                            scope.displayKey = scope.displaykey ? scope.displaykey : scope.dataKey;

                            /*
                             *  Following code has been added to make ng-model-options work
                             *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
                             */
                            if (isNgModelFunction) {
                                try {
                                    ngModelCtrl.$render = function () {
                                        scope.ngModel = ngModelCtrl.$modelValue;
                                        scope.selectedOption = scope.ngModel;
                                    };
                                }
                                catch (e) { }
                            }
                            var updateView = function () {
                                if (isNgModelFunction) {
                                    try {
                                        ngModelCtrl.$setViewValue(scope.ngModel);
                                    }
                                    catch (e) { }
                                }
                            };
                            /*
                             * Check if new option is added
                             */
                            scope.labelVal = "";
                            scope.customOptionReset = false;

                            //To be triggered if option binding is delayed
                            function watchOptions(a_options) {
                                if (a_options && a_options.length > 0) {
                                    var selLabel,
                                        isDisabledOpt = true;
                                    if (scope.label) {
                                        selLabel = scope.label;
                                    } else {
                                        selLabel = $translate.instant('chooseOptionMsg');
                                    }

                                    if (scope.customoption) {
                                        selLabel = scope.customoption;
                                        isDisabledOpt = false;
                                        scope.customOptionReset = true;
                                    }
                                    if (scope.dataKey === scope.displayKey) {
                                        options = [{ title: selLabel, disable: isDisabledOpt, id: 'fakeOptionId' }].concat(a_options);
                                        options[0][scope.displayKey] = options[0].title;
                                    }
                                    else {
                                        var firstOption = { disable: isDisabledOpt, id: 'fakeOptionId' };
                                        firstOption[scope.dataKey] = selLabel;
                                        firstOption[scope.displayKey] = selLabel;
                                        options = [firstOption].concat(a_options)
                                    }
                                    scope.selectOptions = options;
                                    var optionGroups = [],
                                        len = options.length;
                                    for (var i = 0; i < len; i++) {
                                        var tmp = options[i];
                                        if (tmp.hasOwnProperty('groupName') && optionGroups.indexOf(tmp.groupName) == -1) {
                                            optionGroups.push(tmp.groupName);
                                        }
                                    }
                                    scope.optionGroups = angular.copy(optionGroups);
                                    watchModel();
                                    smartSelectService.buildOptionsList(scope.optionGroups, scope.selectOptions,scope,element);
                                } else {
                                    return;
                                };

                            }

                            // This function watches the selected value from the 
                            // dropdown and triggers on selection change
                            function watchModel() {
                                if (!isNgModelWatchAdded) {
                                    onNgModel = scope.$watch(isNgModelFunction ? 'ngModel' : 'ngModelCopy', function (newVal) {
                                        if (angular.isUndefined(typeOfNgModel) && (newVal !== '' && newVal != undefined && newVal != null)) {
                                            typeOfNgModel = typeof newVal;
                                        }
                                        var newValueCopy;
                                        if (typeOfNgModel == 'string' || typeOfNgModel == 'number') {
                                            var len = options.length;
                                            for (var i = 0; i < len; i++) {
                                                var tmp = options[i];
                                                if (newVal == tmp[scope.dataKey]) {
                                                    newValueCopy = tmp;
                                                    break;
                                                }
                                            }
                                        }
                                        else if (typeOfNgModel == 'object') {
                                            scope.ngModel = newValueCopy = newVal;
                                        }
                                        else {
                                            newValueCopy = newVal;
                                        }
                                        scope.selectedOption = angular.copy(newValueCopy);

                                        try {
                                            if (!scope.selectedOption) {
                                                scope.selectedOption = angular.copy(options[0]);
                                            } else if (!scope.selectedOption[scope.displayKey]) {
                                                scope.selectedOption = angular.copy(options[0]);
                                            }
                                            scope.selectedOptionString = angular.copy(scope.selectedOption[scope.dataKey]);
                                            if (options[0] != scope.selectedOption[scope.dataKey]) {
                                                scope.labelVal = scope.selectedOption[scope.dataKey];
                                                if (scope.customoption && scope.customoption != scope.selectedOption[scope.dataKey]) {
                                                    scope.customOptionReset = false;
                                                }
                                            }
                                        }
                                        catch (e) { }
                                        smartSelectService.buildOptionsList(scope.optionGroups, scope.selectOptions, scope, element);
                                        smartSelectService.updateLabelTemplate(scope, element);// added for update the label
                                        angular.element("#" + scope.dropDownId).material_select();
                                        element.parent().find("input.select-dropdown").off("openSelectList").on("openSelectList", function () {
                                            if (this.value !== '' && this.value !== void 0 && this.value !== null) {
                                                setTimeout(function () {
                                                    var $dropdownDiv = element.parent().find(".dropdown-content.select-dropdown.active"),
                                                        listElOffset;
                                                    $dropdownDiv.scrollTop(0);
                                                    listElOffset = $dropdownDiv.find("li").eq(_.indexOf(scope.selectOptions, scope.selectOptions.find(function (obj) {
                                                        return obj[scope.dataKey] == (scope.ngModelCopy && scope.ngModelCopy[scope.dataKey]);
                                                    }))).offset();
                                                    if (listElOffset) {
                                                        $dropdownDiv.scrollTop(listElOffset.top - $dropdownDiv.offset().top);
                                                    }
                                                }, 0);
                                            }
                                        });
                                        //selectFixdd();
                                        element.parent().find("input.select-dropdown").bind("close", function () {
                                            scope.validateRules()
                                        }); 
                                    });
                                    isNgModelWatchAdded = true;

                                } else { //If options are changes dynamically then again .material_select() will fired to update latest options in materialize select.
                                   setTimeout(function () {
                                       angular.element("#" + scope.dropDownId).material_select();
                                       element.parent().find("input.select-dropdown").off("openSelectList").on("openSelectList", function () {
                                           if (this.value !== '' && this.value !== void 0 && this.value !== null) {
                                            setTimeout(function () {
                                                var $dropdownDiv = element.parent().find(".dropdown-content.select-dropdown.active"),
                                        listElOffset;
                                                $dropdownDiv.scrollTop(0);
                                                listElOffset = $dropdownDiv.find("li").eq(_.indexOf(scope.selectOptions, scope.selectOptions.find(function (obj) {
                                                    return obj[scope.dataKey] == (scope.ngModelCopy && scope.ngModelCopy[scope.dataKey]);
                                                }))).offset();
                                                if (listElOffset) {
                                                    $dropdownDiv.scrollTop(listElOffset.top - $dropdownDiv.offset().top);
                                                }
                                            }, 0);
                                               }
                                        });
                                        element.parent().find("input.select-dropdown").bind("close", function () {
                                            scope.validateRules()
                                        });
                                    });
                                }
                            }

                            //watchOptions(scope.options); // onload data binding
                            //To update value incase of late binding
                            var collectionWatch = scope.$watchCollection('options', function (newValue) {
                                if (newValue) {
                                    watchOptions(newValue);
                                }
                            });

                            /*function selectFixdd() {
                                if (attrs.fixed) {
                                    $(element).find('input.select-dropdown').bind('focus', function (e) {
                                        (function ($t) {
                                            var drpContent = $(element).find('.dropdown-content').addClass('fixeddd');
                                            var topOffset = $(e.target).offset().top - $(window).scrollTop();
                                            var ddCnt = $t.next('.dropdown-content');
                                            ddCnt.css({
                                                'left': $(e.target).offset().left + 'px', 'top': topOffset + 'px'
                                            });
                                            ddCnt.attr('style', function (i, s) { return s + 'display: block !important;' });

                                            var ddHgt = ddCnt.css('height'),
                                                winHgt = angular.element(window).height();
                                            if ((parseInt(ddHgt) + topOffset) > winHgt) {
                                                var diffHgt = (parseInt(ddHgt) + topOffset) - winHgt;
                                                ddCnt.css('top', (topOffset - diffHgt - 15) + 'px');
                                            }

                                            angular.element($window).on("scroll.smartFixedSmartSelect", function () {
                                                if (ddCnt.hasClass('active'))
                                                    ddCnt.hide();
                                            });
                                            drpContent = null;
                                        })($(this));
                                    });
                                }
                            }
                            */
                            scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

                            /*
                             * Watch needs to be added on disable since element needs to be updated
                             */
                            var sOption = scope.label;
                            // Use watchgroup to watch all
                            //Below declerations are to make watchGroup work on models
                            scope.disable = (scope.disable) ? scope.disable : 1;
                            scope.validate = (scope.validate) ? scope.validate : 2;
                            var watchGrp = scope.$watchGroup(['disable', 'validate', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
                                if (newVal[0] !== oldVal[0]) {
                                    var el = angular.element("#" + scope.dropDownId);
                                    if ((scope.disable === true && scope.disable !== 1) || (jQuery.type(scope.disable) === "string" && (scope.disable === "true"))) {
                                        el.prop('disabled', 'disabled');
                                        el.prop("readonly", true);
                                        el.attr('tabindex', -1);
                                        el.material_select();
                                        el.parent().find("input.select-dropdown").off("openSelectList").on("openSelectList", function () {
                                            if (this.value !== '' && this.value !== void 0 && this.value !== null) {
                                                setTimeout(function () {
                                                    var $dropdownDiv = el.parent().find(".dropdown-content.select-dropdown.active"),
                                                        listElOffset;
                                                    $dropdownDiv.scrollTop(0);
                                                    listElOffset = $dropdownDiv.find("li").eq(_.indexOf(scope.selectOptions, scope.selectOptions.find(function (obj) {
                                                        return obj[scope.dataKey] == (scope.ngModelCopy && scope.ngModelCopy[scope.dataKey]);
                                                    }))).offset();
                                                    if (listElOffset) {
                                                        $dropdownDiv.scrollTop(listElOffset.top - $dropdownDiv.offset().top);
                                                    }
                                                }, 0);
                                            }
                                        });
                                        el.parent().find("input.select-dropdown").bind("close", function () {
                                            scope.validateRules()
                                        });
                                    }else
                                    {
                                        el.prop('disabled', false);
                                        el.prop("readonly", false);
                                        el.removeAttr('tabindex');
                                    }
                                    angular.element("#" + scope.dropDownId).material_select();
                                    element.parent().find("input.select-dropdown").off("openSelectList").on("openSelectList", function () {
                                        if (this.value !== '' && this.value !== void 0 && this.value !== null) {
                                        setTimeout(function () {
                                            var $dropdownDiv = element.parent().find(".dropdown-content.select-dropdown.active"),
                                                listElOffset;
                                            $dropdownDiv.scrollTop(0);
                                            listElOffset = $dropdownDiv.find("li").eq(_.indexOf(scope.selectOptions, scope.selectOptions.find(function (obj) {
                                                return obj[scope.dataKey] == (scope.ngModelCopy && scope.ngModelCopy[scope.dataKey]);
                                            }))).offset();
                                            if (listElOffset) {
                                                $dropdownDiv.scrollTop(listElOffset.top - $dropdownDiv.offset().top);
                                            }
                                        }, 0);
                                    }
                                    });
                                }
                                if (newVal[1] !== oldVal[1] || newVal[2] !== oldVal[2]) {
                                    scope.validateRules();
                                }
                                if (newVal[3] != oldVal[3] || newVal[4] != oldVal[4]) {
                                    if (scope.showinfo || scope.showwarning) {
                                        element.addClass('field-has-icon');
                                    }
                                    else {
                                        element.removeClass('field-has-icon');
                                    }
                                }

                            var onFocus = scope.$watch('focus', function (newVal) {
                                if (newVal) {
                                    ScrollTo.perform(element, angular.element('#' + scope.parentElement));
                                    scope.focus = false;
                                    var clrMe = setTimeout(function () {
                                        element.find('input').focus();
                                        clearTimeout(clrMe);
                                        clrMe = null;
                                    }, 1000);
                                }
                               });
                            });

                            /*
                            * Validate rules
                            */
                            scope.validateRules = function () {
                                scope.validate = false;
                                scope.errorMessage = null;
                                if (scope.$eval(scope.isMandatory) && scope.label && !scope.clienterror) {
                                    if (!scope.ngModelCopy || scope.ngModelCopy.name === scope.label || (scope.customoption && scope.ngModelCopy.name === scope.customoption) || scope.ngModelCopy[scope.dataKey] === scope.label) {
                                        scope.validate = true;
                                        scope.errorMessage = scope.customMessage ? scope.customMessage : $translate.instant("selectOptionErrMsg");
                                        // return;
                                    }
                                }
                                if (scope.clienterror) {
                                    scope.validate = true;
                                    scope.errorMessage = $translate.instant("clientErrMsg");
                                }
                                if (scope.rules && !scope.clienterror) {
                                    var rules = scope.$eval(scope.rules);
                                    var isFoundInvalid = false,
                                        len = rules.length;

                                    for (var i = 0; i < len; i++) {
                                        var tmp = rules[i];
                                        if (typeof tmp == 'object') {
                                            if (eval((tmp.rule).replace(/this/g, 'scope.selectedOption'))) {
                                                scope.validate = true;
                                                scope.errorMessage = tmp.error;
                                                break;
                                            }
                                        } else {
                                            if (!isFoundInvalid) {
                                                RuleEngine.isValid(tmp, function (e) {
                                                    scope.validate = true;
                                                    scope.errorMessage = e.errorData.error;
                                                    isFoundInvalid = true;
                                                });
                                            }
                                            else {
                                                break;
                                            }
                                        }

                                    }
                                }
                                var containerElm = element;
                                var validateElm = $(element).find('#' + scope.dropDownId + '_validate');
                                if (scope.validate == true && scope.errorMessage != null) {

                                    if (!containerElm.hasClass('invalid')) {
                                        containerElm.addClass('invalid');
                                    }

                                    if (validateElm.length > 0) {
                                        validateElm.html(scope.errorMessage);
                                    }
                                    else {
                                        containerElm.append(smartSelectService.buildValidationTemplate(scope));
                                        validateElm = $(element).find('#' + scope.dropDownId + '_validate');
                                        validateElm.html(scope.errorMessage);
                                    }

                                }
                                else {
                                    if (containerElm.hasClass('invalid')) {
                                        containerElm.removeClass('invalid');
                                    }

                                    if (validateElm.length > 0) {
                                        validateElm.remove();
                                    }

                                }
                            };

                            scope.hideShowField = function () {
                                scope.isVisible = !scope.isVisible;
                            };

                            var onSelectChange = function (elm) {
                                if (scope.selectOptions && scope.selectOptions.length>0) {
                                    var selectedOption;
                                    var selectedVal = elm.target.options[elm.target.options.selectedIndex].value;// elm.find(":selected").val();

                                    selectedOption = selectedVal;

                                    sOption = selectedOption;
                                    if (selectedOption == scope.customoption) {
                                        scope.customOptionReset = true;
                                    }
                                    else {
                                        scope.customOptionReset = false;
                                    }

                                    var tmpSelectedOption;
                                    if (!scope.$eval(scope.isMandatory) && selectedOption == options[0][scope.displayKey] && scope.optionGroups.length == 0) {
                                        tmpSelectedOption = '';
                                    }
                                    else {
                                        var len = options.length;
                                        for (var i = 0; i < len; i++) {
                                            if (typeof options[i][scope.dataKey] == 'string') {
                                                if (options[i][scope.dataKey].replace(/&quot;/g, '"') == selectedOption) {
                                                    tmpSelectedOption = options[i];
                                                    break;
                                                }
                                            }
                                            else {
                                                if (options[i][scope.dataKey] == selectedOption) {
                                                tmpSelectedOption = options[i];
                                                break;
                                            }
                                        }
                                    }
                                    }
                                    if (isNgModelFunction) {
                                        if (typeOfNgModel == 'string' || typeOfNgModel == 'number') {
                                            scope.ngModel = tmpSelectedOption[scope.dataKey];
                                        }
                                        else {
                                            scope.ngModel = tmpSelectedOption;
                                        }
                                    }
                                    else {
                                        if (typeOfNgModel == 'string' || typeOfNgModel == 'number') {
                                            if (!scope.$eval(scope.isMandatory) && (selectedOption == options[0][scope.displayKey] || selectedOption == undefined || selectedOption == 'fakeOptionId')) {
                                                scope.ngModelCopy = '';
                                            }
                                            else {
                                                scope.ngModelCopy = tmpSelectedOption[scope.dataKey];
                                            }
                                        }
                                        else {
                                            scope.ngModelCopy = tmpSelectedOption;
                                            scope.ngModel = scope.ngModelCopy;
                                        }
                                    }
                                    scope.labelVal = "";
                                    if (selectedOption == scope.label && selectedOption == options[0][scope.displayKey]) {
                                        scope.labelVal = selectedOption;
                                    }
                                    updateView();                                    
                                    $(element).find('#' + scope.dropDownId + '_label').html(scope.label + formWidgetUtils.getMandatoryFieldHtml(scope.isMandatory, scope));

                                    scope.selectedOption = tmpSelectedOption == '' ? options[0] : angular.copy(tmpSelectedOption);
                                    scope.validateRules();
                                    $timeout(function () {
                                        scope.$apply();
                                        scope.onChange();
                                    });
                                }
                            };

                            smartSelectService.buildTemplate(scope, element, onSelectChange);
                            if (scope.clienterror) {
                                scope.validateRules();
                            }

                            scope.$on('$destroy', function () {
                                angular.element("#" + scope.dropDownId).material_select('destroy');
                                collectionWatch();
                                watchGrp();
                                angular.element($window).off("scroll.smartFixedSmartSelect");
                                $(element).find('input.select-dropdown').unbind('focus');
                                $(element).find('input.select-dropdown').unbind('close');
                            });
                            setTimeout(function(){
                             if(scope.label != '' && scope.label != undefined)
                                 $(element).find('#' + scope.dropDownId + '_label').html(scope.label + formWidgetUtils.getMandatoryFieldHtml(scope.isMandatory, scope));
                            },0)
                        });
                    }
                }

            }
        }
    }

    angular.module('SMART2').factory('smartSelectService', ['ScrollTo', 'RuleEngine', '$templateCache', '$compile', 'formWidgetUtils', smartSelectServiceFunc])
    function smartSelectServiceFunc(ScrollTo, RuleEngine, $templateCache, $compile, formWidgetUtils) {

        var buildOptionalFieldTemplate = function (scope) {
            if ((scope.isMandatory!= undefined && scope.isMandatory=='false') && scope.isRemovable == true) {
                var optionalField = $templateCache.get('shared/directives/uiElements/smartSelect/smartSelectOptionalFieldTemplate.html');
                optionalField = optionalField.replace('{{id}}', scope.dropDownId + '_opField');
                return optionalField;
            }
            return '';
        };
        var buildSelectTemplate = function (scope) {
            var html = $templateCache.get('shared/directives/uiElements/smartSelect/smartSelectOptionTemplate.html');
            html = html.replace('{{id}}', scope.dropDownId);
            return html;
        };
        var buildValidationTemplate = function (scope) {
            var html = $templateCache.get('shared/directives/uiElements/smartSelect/smartSelectValidationTemplate.html');
            html = html.replace('{{id}}', scope.dropDownId + '_validate');
            return html;
        };
        var updateLabelTemplate = function (scope, element) {
            var elmLabel = element.find('#' + scope.dropDownId + '_label');
            if (scope.label != scope.labelVal) {
                elmLabel.removeClass('ng-hide');
            } else {
                elmLabel.addClass('ng-hide');

            }
        };
        var buildLabelTemplate = function (scope) {
            if (scope.label && scope.label != scope.labelVal) {
                var html = $templateCache.get('shared/directives/uiElements/smartSelect/smartSelectLabelTemplate.html');
                html = html.replace('{{id}}', scope.dropDownId + '_label');
                html = html.replace('{{dropDownId}}', scope.dropDownId);
                html = html.replace('{{label}}', scope.label + formWidgetUtils.getMandatoryFieldHtml(scope.isMandatory, scope));
                return html;
            } else {
                return '';
            };
        };
        var buildInfoTemplate = function (scope) {
            var html = $templateCache.get('shared/directives/uiElements/smartSelect/smartSelectInfoTemplate.html');;
            //if (scope.showinfo || scope.showwarning) {
                
            //    html = $templateCache.get('shared/directives/uiElements/smartSelect/smartSelectInfoTemplate.html');
            //}
            return html;
        };
        var buildTemplate = function (scope,element, onChangeCallback) {
            var container = element // $('<div/>', { id: scope.dropDownId + '_container' });
            
            var css = 'input-field';
            if (scope.isMandatory == 'false' && scope.isRemovable == true) {
                css += ' optionalField line-height-manager';
            }
            if (scope.preview === true) {
                css += ' selectPreview';
            }
            if (!scope.customoption) {
                css += ' defaultSelected';
            }
            if (scope.validate === true && scope.errorMessage) {
                css += ' invalid';
            }
            if (scope.showinfo || scope.showwarning){
                css += ' field-has-icon'
            }
            if (scope.showinfo && scope.showwarning) {
                css += ' field-has-two-icons'
            }
            
            container.attr("id", scope.dropDownId + '_container').addClass(css);

            container.append(buildOptionalFieldTemplate(scope));
            container.append(buildLabelTemplate(scope));
            container.append(buildSelectTemplate(scope));
            container.append(buildValidationTemplate(scope));
            container.append($compile(buildInfoTemplate(scope))(scope));
            //if (scope.showinfo || scope.showwarning) {
            //    container.append($compile(buildInfoTemplate(scope))(scope));
            //}

            element.append(container);
            var elm = $(element).find('#' + scope.dropDownId);
            elm.bind("change", function (el) {
                //console.log(el);
                onChangeCallback(el);
            });
            var optionalField = $(element).find('#' + scope.dropDownId + '_opField');
            if (typeof optionalField != 'undefined' && optionalField.length > 0)
            {
                optionalField.bind("click", scope.hideShowField);

            }

            if (scope.disable === 'true' || scope.disable === true) {
                elm.prop("readonly", true);
                elm.attr("disabled", "disabled");
                elm.attr('tabindex', -1);
            }

        };

        var buildOptionsList = function (OptionGroups, options,scope,element) {
            var optHtml = '';
            if (OptionGroups && OptionGroups.length > 0) {

            }
            else {
                if (options && options.length > 0 && scope.dataKey === scope.displayKey) {
                    angular.forEach(options, function (option) {
                        var selected = '';
                        if (angular.equals(option, scope.selectedOption) || angular.equals(scope.ngModel, option) || angular.equals(scope.ngModelCopy, option) || angular.equals(option[scope.dataKey], scope.selectedOptionString)) {
                            selected = ' selected="selected"';
                        }
                        //if (option.name == scope.label) {
                        //    $(element).find('#' + scope.dropDownId + '_label').html('');                            
                        //}
                        optHtml += '<option value="' + option[scope.dataKey] + '"  ' + (option.disable ? 'disabled="disabled"' : '') + ' ' + selected + '>' + option[scope.displayKey] + '</option>'
                    });
                } else if (options && options.length > 0 && scope.dataKey !== scope.displayKey) {
                    angular.forEach(options, function (option) {
                        var selected = '';
                        if (angular.equals(option, scope.selectedOption) || angular.equals(scope.ngModel, option) || angular.equals(scope.ngModelCopy, option) || angular.equals(option[scope.dataKey], scope.selectedOptionString)) {
                            selected = ' selected="selected"';
                        }
                        optHtml += '<option value="' + option[scope.dataKey] + '"  ' + (option.disable ? 'disabled="disabled"' : '') + ' ' + selected + '>' + option[scope.displayKey] + '</option>'
                    });
                }
            }

            if (options[0][scope.datakey] == scope.label && options[0].disable && scope.datakey == 'title' && (!scope.selectedOption || options[0] == scope.selectedOption)) {
                $(element).find('#' + scope.dropDownId + '_label').html('');
            }

            $(element).find('#' + scope.dropDownId).html(optHtml);
        };

        var service = {
            buildOptionsList: buildOptionsList,
            buildTemplate: buildTemplate,
            buildValidationTemplate: buildValidationTemplate,
            updateLabelTemplate: updateLabelTemplate
        };
        return service;
    }
})();
